import EnterpriseComponent from "../../EnterpriseComponent";
import { applySort, sortFromSpecification } from "../../../../util/documentFilters";

/**
 * The documents the page is showing, as something PlatformTable can render.
 *
 * FED BY THE PAGE, NOT FETCHING FOR ITSELF, and that is the whole point of it.
 *
 * It did fetch: it called /t11/v1/documents on mount, exactly as the page does. Two consequences,
 * and the second is the real one.
 *
 *   The same list was fetched twice -- once by the page for the cards, once by this component for
 *   the rows -- so switching from grid to list cost a request for something already in hand.
 *
 *   AND THE SEARCH DID NOT APPLY TO THE LIST. The page's search box replaces the rows it is
 *   showing; this component knew nothing about it and kept listing everything. Searching narrowed
 *   the cards, switching to list view silently showed the whole tenant back, and the count above
 *   the grid still read "3 matching documents". Two views of "the same list" that were not the same
 *   list.
 *
 * So the page owns the fetch, the search, the persona, the filters AND the order, and hands the
 * finished rows down. Paging stays the table's, served out of the array it was given.
 *
 * SORTING IS THE TABLE'S, and the cards follow it. The table's menu is the control -- it is the
 * one every other list in the suite has, it remembers its choice, and it puts the direction on the
 * column header where people reach for it. What must not happen is the order diverging between the
 * two views, so the chosen specification is reported back up: the page re-sorts the one list and
 * hands it down again, and the grid shows the same order the list does.
 *
 * SORTED HERE AS WELL AS THERE, with the same comparator, and that is not redundant. PlatformTable
 * sets the specification and re-queries in the same breath, before the page's state has flushed, so
 * without this the first draw after choosing a sort pages the array in its OLD order and the
 * corrected one arrives a frame later -- visible, as a flick.
 */
class DocumentEnterpriseComponent extends EnterpriseComponent {

    rows = [];

    /** The last order reported upwards, so an unchanged one is not reported again. */
    reportedSort = null;

    /**
     * The rows to show, from the page.
     *
     * INVALIDATE, WHICH IS WHAT MAKES THE GRID RE-READ THEM. Setting the array alone changes
     * nothing on screen: the table draws from the buffer it was handed last, so a search would
     * narrow the cards and leave the rows exactly as they were.
     */
    setRows = (rows) => {
        this.rows = Array.isArray(rows) ? rows : [];
        this.invalidate();
    };

    /**
     * The order the table asked for, reported to whoever is feeding this component.
     *
     * PlatformTable hands the specification here on its way to re-querying, which makes this the
     * one place that learns about a sort without PlatformTable itself having to grow a callback --
     * and PlatformTable is the same file in four apps, so it does not grow one.
     */
    setSortSpecification = (specification) => {
        super.setSortSpecification(specification);
        if (!this.onSort) return;

        // ONLY WHEN IT ACTUALLY CHANGED, and this is a loop rather than a nicety.
        // PlatformTable re-sets the specification on EVERY invalidation, and the page invalidates
        // this component every time it hands down new rows -- so reporting unconditionally means:
        // notify -> page sets state -> new array -> setRows -> invalidate -> notify, forever, at
        // render speed. It is the same loop the memo in DocumentTable exists to prevent, reached
        // from the other side.
        const chosen = sortFromSpecification(specification);
        const now = chosen ? `${chosen.key}:${chosen.descending}` : "";
        if (now === this.reportedSort) return;
        this.reportedSort = now;
        this.onSort(chosen);
    };

    performQuery = (page, pageSize) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        // PAGE AND PAGE SIZE GO WITH IT. fetchEnterpriseComponentSuccess takes THREE arguments and
        // buffers rows UNDER THE PAGE NUMBER; called with the object alone they are filed under
        // `undefined`, never become the Maps the grid reads, and the table throws
        // "this.state.buffer[i].get is not a function" -- a blank page that tsc and the production
        // build are both perfectly happy with.
        // ORDERED BEFORE IT IS PAGED. Page two of an unsorted array is not page two of a sorted
        // one, so slicing first would hand the reader the right rows in the wrong order and then
        // the wrong rows entirely.
        const chosen = sortFromSpecification(this.getSortSpecification());
        const ordered = chosen ? applySort(this.rows, chosen.key, chosen.descending) : this.rows;

        this.fetchEnterpriseComponentSuccess({
            Count: ordered.length,
            Results: ordered.slice((page - 1) * pageSize, page * pageSize),
        }, page, pageSize);
    };
}

export default DocumentEnterpriseComponent;
